import React from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { Invoice } from '../../types';
import { BankTransaction } from '../../services/bank/camt053';
import { SplitDraft, SplitEvaluation } from '../../services/bank/importPlan';
import { openAmountCents } from '../../services/invoiceLogic';
import { centsFromEuroInput, formatCents } from '../../services/money';
import { RemainderOption } from './RemainderOption';

/**
 * Einen Zahlungseingang auf mehrere Rechnungen verteilen (Sammelzahlung).
 *
 * Je Teil: Rechnung, Betrag und — wenn der Teil weniger ist als offen —
 * was mit dem Rest passiert (offen lassen, Skonto, Sicherheitseinbehalt).
 * Gebucht werden kann erst, wenn die Teile genau den Umsatz ergeben; die
 * Pruefung steht in evaluateSplit (src/services/bank/importPlan.ts).
 */

interface Props {
    rowKey: string;
    transaction: BankTransaction;
    drafts: SplitDraft[];
    evaluation: SplitEvaluation;
    invoicesById: ReadonlyMap<string, Invoice>;
    customerNameOf: (invoice: Invoice) => string;
    disabled: boolean;
    onChange: (drafts: SplitDraft[]) => void;
    onAddPart: () => void;
    onCancel: () => void;
    /** Teil, fuer den gerade die Rechnungssuche offen ist. */
    searchPartKey: string | null;
    onOpenSearch: (partKey: string) => void;
    /** Die Suche selbst — der Dialog haelt ihren Zustand. */
    searchNode: React.ReactNode;
}

export const SplitEditor: React.FC<Props> = ({
    rowKey,
    transaction,
    drafts,
    evaluation,
    invoicesById,
    customerNameOf,
    disabled,
    onChange,
    onAddPart,
    onCancel,
    searchPartKey,
    onOpenSearch,
    searchNode,
}) => {
    const update = (key: string, patch: Partial<SplitDraft>) =>
        onChange(drafts.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));
    const remove = (key: string) => onChange(drafts.filter((draft) => draft.key !== key));
    const balanced = evaluation.restCents === 0;

    return (
        <div className="space-y-2 rounded-md border border-sky-200 bg-sky-50/60 p-3 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-slate-700">
                    Aufteilen: {formatCents(transaction.amountCents)} auf mehrere Rechnungen
                </span>
                <span
                    className={`font-medium tabular-nums ${balanced ? 'text-emerald-700' : 'text-red-600'}`}
                >
                    verteilt {formatCents(evaluation.sumCents)} ·{' '}
                    {balanced
                        ? 'vollständig'
                        : evaluation.restCents > 0
                          ? `noch ${formatCents(evaluation.restCents)} offen`
                          : `${formatCents(-evaluation.restCents)} zu viel`}
                </span>
            </div>

            <div className="space-y-2">
                {drafts.map((draft, index) => {
                    const invoice = draft.invoiceId ? invoicesById.get(draft.invoiceId) : undefined;
                    const amount = centsFromEuroInput(draft.amountInput);
                    return (
                        <div
                            key={draft.key}
                            className="grid gap-2 rounded border border-slate-200 bg-white p-2 sm:grid-cols-[minmax(14rem,1.4fr)_9rem_minmax(14rem,2fr)_auto]"
                        >
                            <div>
                                <span className="block text-[11px] text-slate-500">Teil {index + 1} · Rechnung</span>
                                {invoice ? (
                                    <span className="block font-medium text-slate-700">
                                        {invoice.invoiceNumber ?? 'ohne Nummer'} · offen{' '}
                                        {formatCents(openAmountCents(invoice))}
                                        <span className="block truncate text-[11px] font-normal text-slate-500">
                                            {customerNameOf(invoice)}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="block text-slate-400">noch keine gewählt</span>
                                )}
                                {searchPartKey === draft.key ? (
                                    searchNode
                                ) : (
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => onOpenSearch(draft.key)}
                                        className="mt-0.5 flex items-center gap-1 text-[11px] text-sky-700 hover:underline disabled:opacity-50"
                                    >
                                        <Search className="h-3 w-3" />
                                        {invoice ? 'Andere Rechnung …' : 'Rechnung suchen …'}
                                    </button>
                                )}
                            </div>
                            <label className="block">
                                <span className="block text-[11px] text-slate-500">Betrag €</span>
                                <input
                                    id={`split-${rowKey}-${draft.key}-amount`}
                                    inputMode="decimal"
                                    value={draft.amountInput}
                                    disabled={disabled}
                                    onChange={(e) => update(draft.key, { amountInput: e.target.value })}
                                    className="w-full rounded border border-slate-300 px-1.5 py-1 text-right tabular-nums"
                                />
                            </label>
                            <div>
                                {invoice && (
                                    <RemainderOption
                                        compact
                                        name={`split-${rowKey}-${draft.key}`}
                                        invoice={invoice}
                                        paymentCents={amount}
                                        valueDate={transaction.valueDate}
                                        value={draft.remainder}
                                        onChange={(value) => update(draft.key, { remainder: value })}
                                        disabled={disabled}
                                    />
                                )}
                            </div>
                            <div className="flex items-start justify-end">
                                <button
                                    type="button"
                                    disabled={disabled || drafts.length <= 2}
                                    onClick={() => remove(draft.key)}
                                    title={drafts.length <= 2 ? 'Mindestens zwei Teile' : 'Teil entfernen'}
                                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-30"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {(evaluation.errors.length > 0 || evaluation.warnings.length > 0) && (
                <ul className="space-y-0.5">
                    {evaluation.errors.map((message) => (
                        <li key={`e-${message}`} className="text-red-600">
                            {message}
                        </li>
                    ))}
                    {evaluation.warnings.map((message) => (
                        <li key={`w-${message}`} className="text-amber-700">
                            {message}
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex flex-wrap gap-3">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onAddPart}
                    className="flex items-center gap-1 text-sky-700 hover:underline disabled:opacity-50"
                >
                    <Plus className="h-3.5 w-3.5" /> Weitere Rechnung
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={onCancel}
                    className="text-slate-500 hover:underline disabled:opacity-50"
                >
                    Aufteilung aufheben
                </button>
            </div>
        </div>
    );
};
